/**
 * Pure helpers for the faceplate stale-data treatment.
 *
 * Kept React-free (a `.ts` module) so they run under the `node --test` sweep,
 * which strips TypeScript types but does not transform JSX. The faceplate
 * components import these so the rendered badge and the unit tests share one
 * source of truth for what counts as a valid `staleSince` and how the elapsed
 * delta is formatted.
 */

/**
 * Accepted shapes for the `staleSince` prop. A faceplate is considered stale
 * the moment a `staleSince` timestamp is supplied: callers pass the
 * last-update timestamp of the real-time topic (the `__freshAt` field on
 * Tyndo response envelopes, the SignalR message timestamp, or a Date) only
 * once they have decided the value can no longer be trusted — e.g. on PLC
 * comms loss or when a freshness threshold has elapsed. When `null` /
 * `undefined`, the faceplate renders normally.
 */
export type StaleSince = Date | string | number | null | undefined;

/** Parse a `StaleSince` into a valid Date, or null when absent/unparseable. */
export function toStaleDate(input: StaleSince): Date | null {
  if (input === null || input === undefined) return null;
  if (input instanceof Date) return Number.isFinite(input.getTime()) ? input : null;
  const d = new Date(input);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** Compact elapsed-time label (`12s`, `4m`, `2h`, `3d`) for the STALE badge. */
export function formatStaleDelta(deltaMs: number): string {
  const s = Math.max(0, Math.round(deltaMs / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}
